// สรุปอุปกรณ์ / เครือข่าย / สัญญาณด้านความปลอดภัย จากการเข้าระบบย้อนหลัง
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import axiosInstance from "../../services/axiosInterceptor";
import {
  DEVICE_LABEL, DistributionTable, RANGE_OPTIONS, REASON_LABEL, SOURCE_LABEL, StatTile,
  countryName, formatHour, num,
} from "./shared";

const API = process.env.REACT_APP_API;

const EMPTY = {
  totals: {}, byOs: [], byBrowser: [], byDevice: [], byCountry: [],
  byHour: [], byFailReason: [], topFailedUsers: [], multiIpAccounts: [], foreignLogins: [],
};

export default function DevicesTab() {
  const [data, setData] = useState(EMPTY);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    axiosInstance
      .get(`${API}/admin/usage/summary`, { params: { days } })
      .then((res) => setData({ ...EMPTY, ...res.data }))
      .catch(() => setData(EMPTY))
      .finally(() => setBusy(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const t = data.totals || {};
  const successes = num(t.successes);
  const inSchool  = num(t.in_school);
  const abroad    = num(t.foreign_logins);

  // แปลงรหัสประเทศเป็นชื่อไทยตั้งแต่ตรงนี้ เพื่อให้ DistributionTable ใช้ร่วมกับตารางอื่นได้
  const countryRows = useMemo(
    () => data.byCountry.map((r) => ({ name: countryName(r.name), total: r.total })),
    [data.byCountry]
  );

  // 5 ชั่วโมงที่มีการเข้าระบบมากที่สุด — ใช้เลือกเวลาที่ปลอดคนสำหรับสำรองข้อมูล/ปิดปรับปรุง
  const peakHours = useMemo(
    () => [...data.byHour].sort((a, b) => num(b.total) - num(a.total)).slice(0, 5),
    [data.byHour]
  );

  const networkRows = [
    { name: 'ในโรงเรียน',  total: inSchool },
    { name: 'นอกโรงเรียน', total: num(t.off_school) },
  ].filter((r) => r.total > 0);

  return (
    <React.Fragment>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <Form.Select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 180 }}>
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        <Button size="sm" variant="outline-primary" onClick={load} disabled={busy}>
          <i className="ri-refresh-line me-1"></i>รีเฟรช
        </Button>
      </div>

      <Row className="g-2 g-lg-3 mb-3">
        <StatTile label="เข้าระบบสำเร็จ"      value={successes.toLocaleString()} variant="success" />
        <StatTile label="เข้าไม่สำเร็จ"       value={num(t.failures).toLocaleString()} variant="danger" />
        <StatTile label="ผู้ใช้ที่เข้าระบบ"   value={num(t.active_users).toLocaleString()} variant="dark" />
        <StatTile
          label="เข้าจากในโรงเรียน"
          value={successes ? `${Math.round((inSchool / successes) * 100)}%` : '—'}
          variant="info"
          hint={`${inSchool.toLocaleString()} จาก ${successes.toLocaleString()} ครั้ง`}
        />
      </Row>

      <Row className="g-2 g-lg-3 mb-3">
        <StatTile
          label="เข้าสำเร็จจากนอกประเทศ"
          value={abroad.toLocaleString()}
          variant={abroad ? 'danger' : 'success'}
          hint={abroad ? 'ตรวจรายการด้านล่างทุกแถว' : 'ไม่พบในช่วงเวลานี้'}
        />
        <StatTile
          label="พยายามเข้าจากนอกประเทศ"
          value={num(t.foreign_failures).toLocaleString()}
          variant="secondary"
          hint="เข้าไม่สำเร็จ — ส่วนใหญ่คือบอทไล่เดารหัส"
        />
      </Row>

      <Row className="g-2 g-lg-3 mb-3">
        <Col md={6} xl={3}><DistributionTable title="ระบบปฏิบัติการ" rows={data.byOs} /></Col>
        <Col md={6} xl={3}><DistributionTable title="เบราว์เซอร์" rows={data.byBrowser} /></Col>
        <Col md={6} xl={3}><DistributionTable title="ประเภทเครื่อง" rows={data.byDevice} labelMap={DEVICE_LABEL} /></Col>
        <Col md={6} xl={3}><DistributionTable title="เครือข่ายที่ใช้เข้าระบบ" rows={networkRows} /></Col>
      </Row>

      <Row className="g-2 g-lg-3 mb-3">
        <Col lg={6}>
          <DistributionTable
            title="ประเทศต้นทาง"
            rows={countryRows}
            emptyText="ยังไม่มีข้อมูล — ต้องติดตั้งฐานข้อมูล GeoIP บนเซิร์ฟเวอร์ก่อน"
          />
          {/* สัญญาอนุญาตของ GeoLite2 บังคับให้แสดงที่มาของข้อมูลในหน้าที่แสดงผลลัพธ์ */}
          <div className="text-secondary fs-xs mt-2">
            ข้อมูลประเทศและผู้ให้บริการจาก GeoLite2 ของ{' '}
            <a href="https://www.maxmind.com" target="_blank" rel="noreferrer">MaxMind</a>
            {' '}— ระบุได้ระดับประเทศเท่านั้น ไม่ระบุจังหวัด และผู้ใช้ VPN จะขึ้นเป็นประเทศของ VPN
          </div>
        </Col>
        <Col lg={6}>
          <Card className="card-one h-100">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">การเข้าระบบจากนอกประเทศ</Card.Title>
              <span className="text-secondary fs-xs">
                เฉพาะที่เข้าสำเร็จ — ถ้าเจ้าตัวไม่ได้อยู่ต่างประเทศ ให้รีเซ็ตรหัสผ่านทันที
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
                <Table hover className="mb-0">
                  <tbody>
                    {data.foreignLogins.length === 0 ? (
                      <tr>
                        <td className="text-center text-secondary py-4 fs-sm">
                          ไม่มีการเข้าระบบจากนอกประเทศในช่วงนี้
                        </td>
                      </tr>
                    ) : data.foreignLogins.map((r) => (
                      <tr key={r.ll_id}>
                        <td className="fs-sm text-nowrap align-middle" style={{ width: 120 }}>
                          {r.ll_time_text}
                        </td>
                        <td className="align-middle">
                          <div>{r.t_name || r.st_name || r.ll_username}</div>
                          <div className="text-secondary fs-xs">
                            {SOURCE_LABEL[r.ll_source] || r.ll_source} · {r.ll_ip}
                            {r.ll_isp ? ` · ${r.ll_isp}` : ''}
                          </div>
                        </td>
                        <td className="text-end align-middle" style={{ width: 130 }}>
                          <Badge bg="danger">{countryName(r.ll_country)}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 g-lg-3 mb-3">
        <Col lg={6}>
          <Card className="card-one h-100">
            <Card.Header><Card.Title as="h6" className="mb-0">ช่วงเวลาที่เข้าระบบมากที่สุด</Card.Title></Card.Header>
            <Card.Body className="p-0">
              {peakHours.length === 0 ? (
                <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีข้อมูล</div>
              ) : (
                <Table hover className="mb-0">
                  <tbody>
                    {peakHours.map((h) => (
                      <tr key={h.hour}>
                        <td>{formatHour(h.hour)}</td>
                        <td className="text-end text-nowrap">{num(h.total).toLocaleString()} ครั้ง</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <DistributionTable
            title="สาเหตุที่เข้าระบบไม่สำเร็จ"
            rows={data.byFailReason.map((r) => ({ name: r.reason, total: r.total }))}
            labelMap={REASON_LABEL}
            emptyText="ไม่มีการเข้าระบบที่ล้มเหลวในช่วงนี้"
          />
        </Col>
      </Row>

      <Card className="card-one mb-3">
        <Card.Header>
          <Card.Title as="h6" className="mb-0">ผู้ใช้ที่เข้าระบบไม่สำเร็จบ่อย</Card.Title>
          <span className="text-secondary fs-xs">ตั้งแต่ 3 ครั้งขึ้นไป — ลืมรหัสผ่าน หรือมีคนพยายามเดารหัส</span>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>รหัสผู้ใช้ที่ใช้เข้าระบบ</th>
                  <th>ชื่อ</th>
                  <th className="text-end" style={{ width: 110 }}>จำนวนครั้ง</th>
                  <th className="text-end" style={{ width: 130 }}>จำนวนที่อยู่</th>
                  <th style={{ width: 140 }}>ครั้งล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {data.topFailedUsers.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-secondary py-4">ไม่มีรายการ</td></tr>
                ) : data.topFailedUsers.map((r) => (
                  <tr key={r.ll_username}>
                    <td className="align-middle fw-semibold">{r.ll_username}</td>
                    <td className="align-middle">
                      {r.t_name || <span className="text-secondary">ไม่มีบัญชีนี้ในระบบ</span>}
                    </td>
                    <td className="align-middle text-end">
                      <Badge bg={num(r.fails) >= 10 ? 'danger' : 'warning'}>{num(r.fails)}</Badge>
                    </td>
                    <td className="align-middle text-end">{num(r.ip_count)}</td>
                    <td className="align-middle fs-sm text-nowrap">{r.last_try}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="card-one">
        <Card.Header>
          <Card.Title as="h6" className="mb-0">บัญชีที่เข้าระบบจากหลายที่อยู่</Card.Title>
          <span className="text-secondary fs-xs">
            ตั้งแต่ 3 ที่อยู่ขึ้นไป — อาจเป็นการใช้หลายเครื่องตามปกติ หรือมีการแชร์รหัสผ่าน
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 110 }}>รหัสผู้ใช้</th>
                  <th>ชื่อ - นามสกุล</th>
                  <th className="text-end" style={{ width: 130 }}>จำนวนที่อยู่</th>
                  <th className="text-end" style={{ width: 130 }}>เข้าระบบ</th>
                </tr>
              </thead>
              <tbody>
                {data.multiIpAccounts.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-secondary py-4">ไม่มีรายการ</td></tr>
                ) : data.multiIpAccounts.map((r) => (
                  <tr key={r.tb_teacher_t_id}>
                    <td className="align-middle fw-semibold">{r.t_idnumber}</td>
                    <td className="align-middle">{r.t_name}</td>
                    <td className="align-middle text-end">
                      <Badge bg={num(r.ip_count) >= 6 ? 'warning' : 'secondary'}>{num(r.ip_count)}</Badge>
                    </td>
                    <td className="align-middle text-end">{num(r.logins).toLocaleString()} ครั้ง</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
